import { ComposerTranslation } from 'vue-i18n'
import {
  Maybe,
  RelationKind,
  Scalars,
  TagRelation,
} from '../__generated__/generated-gql-types'
import { ContentKind } from '../models/api'
import { TagRelations } from '/@src/models/content'

export function getFileExtension(file: File): string {
  // 此处为mime
  const type = file.type
  if (!type) {
    return ''
  }
  const tempType = type.split('/')[1].toLowerCase().trim()
  if (tempType === 'jpeg') {
    return 'jpg'
  } else if (tempType === 'png') {
    return 'png'
  } else {
    let ret = tempType
    switch (tempType) {
      case 'x-rar':
        ret = 'rar'
        break
      case 'mpeg':
        ret = file.name.substring(file.name.lastIndexOf('.'))
        break
      case 'x-m4a':
        ret = 'm4a'
        break
      case 'vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        ret = 'xlsx'
        break
      case 'vnd.ms-excel':
        ret = 'xls'
        break
      case 'vnd.ms-excel.sheet.macroenabled.12':
        ret = 'xlsm'
        break
      case 'msword':
        ret = 'doc'
        break
      case 'vnd.openxmlformats-officedocument.wordprocessingml.document':
        ret = 'docx'
        break
      case 'vnd.ms-powerpoint':
        ret = 'ppt'
        break
      case 'vnd.openxmlformats-officedocument.presentationml.presentation':
        ret = 'pptx'
        break
      case 'vnd.ms-powerpoint.presentation.macroenabled.12':
        ret = 'pptm'
        break
    }
    return ret
  }
}

export function getPageTitle(
  t: ComposerTranslation,
  pageTitle: string
): string {
  return t(pageTitle) + ' - ' + t('header-page-suffix')
}

export function parseDurationText(seconds: number): string {
  const hour = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds - hour * 3600) / 60)
  const sec = seconds - hour * 3600 - minutes * 60
  let hourStr: string = ''
  let minStr: string
  let secStr: string
  if (hour > 0) {
    hourStr = hour > 9 ? hour.toString() : '0' + hour
  }
  if (minutes == 0) {
    minStr = '00'
  } else {
    minStr = minutes > 9 ? minutes.toString() : '0' + minutes
  }
  if (sec == 0) {
    secStr = '00'
  } else {
    secStr = sec > 9 ? sec.toString() : '0' + sec
  }
  let time
  if (hourStr) {
    time = hourStr + ':' + minStr + ':' + secStr
  } else {
    time = minStr + ':' + secStr
  }
  return time
}

export function getDefaultLanguage(language: string = 'zh-CN'): string {
  let defaultLanguage = (navigator?.language || 'zh-CN').toLowerCase()
  if (defaultLanguage === 'zh' || defaultLanguage === 'zh-cn') {
    defaultLanguage = 'zh-CN'
  } else if (defaultLanguage === 'en' || defaultLanguage === 'en-us') {
    // 适配en-xxx的情况
    defaultLanguage = 'en'
  } else {
    defaultLanguage = language
  }
  return defaultLanguage
}

export function isNumber(text: string): boolean {
  const n = Number(text)
  let ret = false
  if (!isNaN(n)) {
    ret = true
  }
  return ret
}

export const isExtensionPicture = (extension: string): boolean => {
  const data = extension.replaceAll('.', '').toLowerCase()
  return ['jpg', 'png', 'jpeg'].indexOf(data) >= 0
}

export const isExtensionReport = (extension: string): boolean => {
  const data = extension.replaceAll('.', '').toLowerCase()
  return ['pdf', 'doc', 'docx', 'xls', 'xlsx'].indexOf(data) >= 0
}
/**
 * 返回去除后缀名的文件名
 * @param filename
 */
export const getNameWithoutExtension = (filename: string): string => {
  const index = filename.lastIndexOf('.')
  if (index > 0) {
    return filename.substring(0, index)
  } else {
    return filename
  }
}

export const getQueryString = (name: string): string => {
  const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i')

  const r = window.location.search.substr(1).match(reg)

  if (r != null) {
    return unescape(r[2])
  }

  return ''
}

/*
 * 总数
 * */
export function sum(arr: number[]) {
  return arr.reduce((prev, curr) => {
    return prev + curr
  })
}

export function getRandomNumber(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

export function compareObjects(obj1: any, obj2: any) {
  // Get the keys of both objects
  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  // Compare the number of keys
  if (keys1.length !== keys2.length) {
    return false
  }

  // Compare the values of each key
  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) {
      return false
    }
  }

  return true
}

/**
 * 获取表单对象中的值
 * @param obj
 * @param keyString
 * @param index
 * @param ignoreUndefined 当值为undefined时，是否忽略不创建新对象，如果为false时，当前对象为空会创建对象
 */
export function getNestedField<T>(
  obj: any,
  keyString: string,
  index: number = -1,
  ignoreUndefined: boolean = false
): T | undefined {
  if (!obj) return undefined
  const keys = keyString.split('.')
  if (keyString === '') {
    return obj as T
  }
  let target = obj
  if (index >= 0) {
    target = obj[index]
  }
  if (!target) return
  return keys.reduce((acc, key, currentIndex) => {
    const val = acc?.[key]
    if (val === undefined) {
      if (ignoreUndefined) {
        return acc
      }
      if (currentIndex === keys.length - 1) {
        acc[key] = undefined
      } else {
        const nextKey = keys[currentIndex + 1]
        if (isNumber(nextKey)) {
          acc[key] = []
        } else {
          acc[key] = {}
        }
      }
    }
    return acc[key]
    // return (acc && acc[key] !== undefined) ? acc[key] : undefined
  }, target) as T
}

export function setNestedField(obj: any, keyString: string, value: any) {
  const keys = keyString.split('.')
  keys.reduce((acc, key, index, arr) => {
    if (index === arr.length - 1) {
      acc[key] = value
    } else {
      if (!acc[key] || typeof acc[key] !== 'object') {
        acc[key] = {}
      }
    }
    return acc[key]
  }, obj)
}

export function areArraysEqual<T>(
  arr1: T[],
  arr2: T[],
  isDeep: boolean = false
): boolean {
  if (arr1.length !== arr2.length) return false

  if (isDeep) {
    return arr1.every((value, index) => deepEqual(value, arr2[index]))
  } else {
    return arr1.every((value, index) => value === arr2[index])
  }
}

function deepEqual(a: any, b: any): boolean {
  if (a === b) return true

  if (Array.isArray(a) && Array.isArray(b)) {
    return areArraysEqual(a, b, true)
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const keys = Object.keys(a)
    if (keys.length !== Object.keys(b).length) return false
    for (const key of keys) {
      if (!deepEqual(a[key], b[key])) return false
    }
    return true
  }

  return false
}

/**
 * 从对象中祛除typename
 * @param data
 */
export function excludeTypename<T>(data: T): T {
  const handler = (key: string, value: any) =>
    key === '__typename' ? undefined : value
  return JSON.parse(JSON.stringify(data), handler)
}

export function generateRandomString(length: number) {
  const array = new Uint8Array(length)
  window.crypto.getRandomValues(array)
  return Array.from(array, (byte) =>
    ('0' + (byte & 0xff).toString(16)).slice(-2)
  )
    .join('')
    .slice(0, length)
}
/**
 * 转换kind Array
 */
type Relations = {
  group?: Maybe<Scalars['Int']['output']>
  id?: Maybe<Scalars['Int']['output']>
  k?: Maybe<string | number>
}

export function convertRelationsKind(relations: Relations[]): TagRelations[] {
  return relations.map((item) => {
    const newItem = { ...item }
    switch (item.k) {
      case RelationKind.None:
        newItem.k = 0
        break
      case RelationKind.CalendarTopic:
        newItem.k = ContentKind.CALENDAR_TOPIC
        break
      case RelationKind.Tag:
        newItem.k = ContentKind.TAG
        break
      case RelationKind.ExternalSource:
        newItem.k = ContentKind.EXTERNAL_SOURCE
        break
      case RelationKind.ExternalSourceGroup:
        newItem.k = ContentKind.EXTERNAL_SOURCE_GROUP
        break
      case RelationKind.Topic:
        newItem.k = ContentKind.TOPIC
        break
    }
    return newItem
  })
}

export function getRelationKindString(k: number) {
  switch (k) {
    case 0:
      return RelationKind.None
    case ContentKind.CALENDAR_TOPIC:
      return RelationKind.CalendarTopic
    case ContentKind.TAG:
      return RelationKind.Tag
    case ContentKind.EXTERNAL_SOURCE:
      return RelationKind.ExternalSource
    case ContentKind.EXTERNAL_SOURCE_GROUP:
      return RelationKind.ExternalSourceGroup
    case ContentKind.TOPIC:
      return RelationKind.Topic
    default:
      return RelationKind.None
  }
}

export function formatNumberWithMinUnit(num: number, decimalPlaces: number) {
  // 四舍五入到指定的小数位数
  const roundedNum =
    Math.round(num * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces)

  // 将数字转换为字符串
  let numStr = roundedNum.toFixed(decimalPlaces)

  // 检查是否已有足够的小数位
  const decimalPart = numStr.split('.')[1] || ''

  // 如果最后一位为零，则替换为1
  if (decimalPart.endsWith('0')) {
    numStr = numStr.substring(0, numStr.length - 1) + '1'
  }

  return numStr
}

export interface FormatterOption {
  numberFormat?: 's' | 'z'
  prefix?: string
  suffix?: string
  precision?: number
  separator?: 'none' | ',' | '_' | ' '
}
/**
 * 更新format字符串
 * patten的格式为 prefix[[.#|,#|~#|.*|,*][%|s|b|n][/<char>]]suffix
 *                      precision scale      separator
 * []内第一部分为小数点精度默认为".1"  ".*"表示任意小数
 *  "[.0]", assert_eq!(f.fmt(10.234), "10");
 *  "[.2]"，assert_eq!(f.fmt(1.2345), "1.23");
 * 第二部分为scale:
 *  s for SI scaling (Scales::short),
 *  % for percentage scaling (Formatter::percentage),
 *  m for metric scaling (Scales::metric),
 *  b for binary scaling (Scales::binary),
 *  n for no scaling (Scales::none)
 * 第三部分为分隔符:
 *  "[n]" 表示显示千分位",", assert_eq!(f.fmt(12345.0), "12,345.0");
 *  "[n/]" 表示不显示千分位, assert_eq!(f.fmt(12345.0), "12345.0");
 *  "[n/_]" 表示千分位显示为 "_",  assert_eq!(f.fmt(12345.0), "12_345.0");
 *  "[n/ ]" 表示使用空格分割，assert_eq!(f.fmt(12345.0), "12 345.0");
 *  "[n/.]" 表示使用. 分割，此时小数分割为”,“, assert_eq!(f.fmt(12345.0), "12.345,0");
 *
 *  例子：
 *  [.2n] assert_eq!(f.fmt(12345.234), "12,345.23");
 *  [.2n/] assert_eq!(f.fmt(12345.234), "12345.23");
 *  [.0n/_] assert_eq!(f.fmt(12345.234), "12_345");
 * @param oldPatten
 * @param option FormatterOption
 *
 */
export function updateFormatter(
  oldPatten: string,
  option: FormatterOption
): string {
  let newPatten = oldPatten
  if (option.prefix !== undefined) {
    const start = oldPatten.indexOf('[')
    if (start > 0) {
      newPatten = option.prefix + oldPatten.substring(start)
    } else {
      newPatten = option.prefix + newPatten
    }
  }
  if (option.suffix !== undefined) {
    const start = oldPatten.indexOf(']')
    if (start > 0) {
      newPatten = newPatten.substring(0, start + 1) + option.suffix
    } else {
      newPatten = newPatten + option.suffix
    }
  }
  // 如果有数值单位格式化时，只保留前后缀
  if (option.numberFormat) {
    newPatten = newPatten.replace(/\[.*\]/g, `[${option.numberFormat}]`)
    return newPatten
  } else {
    if (oldPatten.includes('[s]') || oldPatten.includes('[z]')) {
      newPatten = newPatten.replace(/\[.*\]/g, '[.2n]')
    }
  }
  if (option.precision !== undefined) {
    newPatten = newPatten.replace(/\.\d+n/g, '.' + option.precision + 'n')
  }
  if (option.separator) {
    // 逗号时，使用默认千分位，即prefix[.xn]suffix
    if (option.separator === ',') {
      newPatten = newPatten.replace(/\[\.(\d)n[^\]]*\]/g, '[.$1n]')
    } else if (option.separator === 'none') {
      newPatten = newPatten.replace(/\[\.(\d)n[^\]]*\]/g, '[.$1n/]')
    } else {
      newPatten = newPatten.replace(
        /\[\.(\d)n[^\]]*\]/g,
        `[.$1n/${option.separator}]`
      )
    }
  }
  return newPatten
}
